import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { scoreToGrade } from '../utils/GradeUtils';
import { parseAuditModules, AuditModule } from '../utils/parseAuditModules';
import { parseModuleChecks, Check } from '../utils/parseModuleChecks';
import PixelGradeBadge from './PixelGradeBadge';
import { useToast } from './Toast';

// Normalized severity for triage ordering
type Severity = 'fail' | 'warn' | 'info' | 'pass' | 'error';

const SEVERITY_RANK: Record<Severity, number> = { fail: 0, error: 1, warn: 2, info: 3, pass: 4 };

const normalizeStatus = (status: string): Severity => {
  const s = (status || '').toLowerCase();
  if (['red', 'fail', 'critical', 'failed'].includes(s)) return 'fail';
  if (['yellow', 'warn', 'warning', 'weak'].includes(s)) return 'warn';
  if (['green', 'pass', 'success', 'compliant', 'ok'].includes(s)) return 'pass';
  if (['blue', 'error', 'unknown'].includes(s)) return 'error';
  if (s === 'info') return 'info';
  return 'error';
};

const severityStyles: Record<Severity, { label: string; text: string; dot: string; bg: string }> = {
  fail: { label: 'FAILED', text: 'text-red-400', dot: 'bg-red-400', bg: 'bg-red-400/10' },
  error: { label: 'ERROR', text: 'text-blue-400', dot: 'bg-blue-400', bg: 'bg-blue-400/10' },
  warn: { label: 'WARNING', text: 'text-yellow-400', dot: 'bg-yellow-400', bg: 'bg-yellow-400/10' },
  info: { label: 'INFO', text: 'text-sky-300', dot: 'bg-sky-300', bg: 'bg-sky-300/10' },
  pass: { label: 'PASS', text: 'text-green-400', dot: 'bg-green-400', bg: 'bg-green-400/10' },
};

const checkDot: Record<Check['status'], string> = {
  pass: 'bg-green-400',
  warn: 'bg-yellow-400',
  fail: 'bg-red-400',
  info: 'bg-white/30',
  unassessed: 'bg-slate-400',
};

// Remediation guidance shown in the detail panel, keyed by module-name substring
const REMEDIATION_HINTS: Array<{ match: RegExp; hint: string }> = [
  { match: /usb|removable/i, hint: 'Set the removable-storage device policy to deny write access, then push via GPO or Intune and re-run the module to confirm.' },
  { match: /local admin|administrator/i, hint: 'Review the Administrators group membership and remove unexpected accounts. Baseline expects the built-in Administrator disabled plus one managed admin.' },
  { match: /password/i, hint: 'Raise the minimum password length to 12+ via domain or local policy and verify complexity requirements remain enabled.' },
  { match: /registry/i, hint: 'Compare flagged autostart entries against the approved baseline; remove or sign unknown entries and re-audit.' },
  { match: /firewall/i, hint: 'Re-enable all firewall profiles and reconcile rule changes against the approved rule set.' },
  { match: /bitlocker|encrypt/i, hint: 'Enable BitLocker on all fixed drives (AES-256) and escrow recovery keys before re-running the module.' },
  { match: /update/i, hint: 'Approve and install outstanding critical updates, then re-run the module to clear the finding.' },
];

const remediationFor = (moduleName: string): string => {
  const found = REMEDIATION_HINTS.find(h => h.match.test(moduleName));
  return found?.hint || 'Review the failing checks below, apply the corrective configuration on the target machine, and re-run the module to verify.';
};

interface ModuleNode extends AuditModule {
  severity: Severity;
  checks: Check[];
}

interface AgentNode {
  id: string;
  hostname: string;
  online: boolean;
  status: string;
  agentVersion?: string;
  lastSeenAt?: string;
  score: number;
  lastRun?: string;
  modules: ModuleNode[];
  auditError?: string;
  executionId?: string;
  detailLoaded?: boolean;
}

const findingKey = (agentId: string, nodeId: string) => `${agentId}::${nodeId}`;

const daysAgo = (iso?: string): string => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// Small worker pool: same result as Promise.all(items.map(fn)) but with bounded
// concurrency — large fleets must not burst the API rate limit (200 req/min/IP)
async function mapPool<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return results;
}

interface ClientDetailPageProps {
  /** When set, overrides the route param — used by PostureTriagePage to embed this view */
  clientId?: string;
  /** Embedded mode hides the page-level header (back button + client hero) */
  embedded?: boolean;
}

const ClientDetailPage: React.FC<ClientDetailPageProps> = ({ clientId: clientIdProp, embedded = false }) => {
  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const clientId = clientIdProp ?? clientIdParam;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [clientInfo, setClientInfo] = useState<any>(null);
  const [agents, setAgents] = useState<AgentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [showPassing, setShowPassing] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  const [fleetQueued, setFleetQueued] = useState(false);
  const fleetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => () => { if (fleetTimer.current) clearTimeout(fleetTimer.current); }, []);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    // Reset all view state — in embedded mode this effect re-runs on client-tab
    // switches, not just on fresh mounts
    setLoading(true);
    setLoadError(null);
    setClientInfo(null);
    setAgents([]);
    setExpandedAgents(new Set());
    setExpandedModules(new Set());
    setShowPassing(new Set());
    setSelectedKey(null);
    setResolved(new Set());

    const load = async () => {
      try {
        // 3 requests total regardless of fleet size: the latest compliance_scores row
        // per agent (module_scores + execution_id) bootstraps the whole tree; the full
        // audit blob is lazy-loaded per agent only when its detail is actually viewed.
        const [summary, allAgents, clientScores] = await Promise.all([
          api.getComplianceSummary().catch(() => null),
          api.getAgents(),
          api.getClientScores(clientId).catch(() => []),
        ]);

        const client = summary?.clients?.find((c: any) => c.clientId === clientId) || null;
        const clientAgents = (allAgents || []).filter(
          (a: any) => a.client?.id === clientId || a.clientId === clientId || a.client_id === clientId
        );

        const scoreRows: any[] = Array.isArray(clientScores) ? clientScores : [];
        const scoreByAgent = new Map(scoreRows.map((r: any) => [r.agent_id, r]));

        const nodes: AgentNode[] = clientAgents.map((a: any): AgentNode => {
            const base: AgentNode = {
              id: a.id,
              hostname: a.hostname || a.name || a.id,
              online: a.status === 'ONLINE',
              status: a.status || 'UNKNOWN',
              agentVersion: a.agentVersion || a.agent_version,
              lastSeenAt: a.lastSeenAt || a.last_seen_at || a.lastHeartbeat,
              score: 0,
              modules: [],
            };
            const row = scoreByAgent.get(a.id);
            if (!row) return base;
            try {
              // results: {} is required — parseAuditModules bails on a missing results
              // key before it ever reaches the scoring fallback chain
              const modules = parseAuditModules({
                results: {},
                scoring: { module_scores: row.module_scores, cis_scores: row.cis_scores },
              }).map((m): ModuleNode => ({
                ...m,
                severity: normalizeStatus(m.status),
                checks: parseModuleChecks(m.details),
              }));
              // Triage order: worst first, then by name for stability
              modules.sort((x, y) =>
                SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity] || x.name.localeCompare(y.name)
              );
              return {
                ...base,
                score: Number(row.overall_score) || 0,
                lastRun: row.scored_at,
                modules,
                executionId: row.execution_id,
                detailLoaded: false,
              };
            } catch (err: any) {
              return { ...base, auditError: err?.message || 'Failed to parse compliance scores' };
            }
        });

        // Agents with findings first, then online before offline, then by hostname.
        // Agents whose audit failed to load rank alongside failures — unknown posture
        // is a triage item, not something to bury at the bottom.
        const worst = (n: AgentNode) => n.auditError ? 0 : Math.min(...n.modules.map(m => SEVERITY_RANK[m.severity]), 5);
        nodes.sort((x, y) =>
          worst(x) - worst(y) || Number(y.online) - Number(x.online) || x.hostname.localeCompare(y.hostname)
        );

        if (cancelled) return;
        setClientInfo(client);
        setAgents(nodes);

        // Pre-expand: agents that have findings, and their non-passing modules
        const agentsToExpand = new Set<string>();
        const modulesToExpand = new Set<string>();
        let firstFinding: string | null = null;
        for (const n of nodes) {
          if (n.auditError) agentsToExpand.add(n.id);
          for (const m of n.modules) {
            if (m.severity === 'fail' || m.severity === 'error' || m.severity === 'warn') {
              agentsToExpand.add(n.id);
              modulesToExpand.add(findingKey(n.id, m.nodeId));
              if (!firstFinding) firstFinding = findingKey(n.id, m.nodeId);
            }
          }
        }
        // If the fleet is clean, still open the first agent
        if (agentsToExpand.size === 0 && nodes.length > 0) agentsToExpand.add(nodes[0].id);
        setExpandedAgents(agentsToExpand);
        setExpandedModules(modulesToExpand);
        setSelectedKey(firstFinding);
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load client');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [clientId]);

  const findings = useMemo(() => {
    const list: Array<{ key: string; agent: AgentNode; module: ModuleNode }> = [];
    for (const a of agents) {
      for (const m of a.modules) {
        if (m.severity === 'fail' || m.severity === 'error' || m.severity === 'warn' || m.severity === 'info') {
          list.push({ key: findingKey(a.id, m.nodeId), agent: a, module: m });
        }
      }
    }
    list.sort((x, y) => SEVERITY_RANK[x.module.severity] - SEVERITY_RANK[y.module.severity]);
    return list;
  }, [agents]);

  const actionable = useMemo(
    () => findings.filter(f => f.module.severity !== 'info'),
    [findings]
  );
  const loadFailures = useMemo(() => agents.filter(a => a.auditError), [agents]);
  const resolvedCount = actionable.filter(f => resolved.has(f.key)).length;

  const rollup = useMemo(() => {
    let pass = 0, warn = 0, fail = 0, info = 0, error = 0;
    for (const a of agents) {
      for (const m of a.modules) {
        if (m.severity === 'pass') pass++;
        else if (m.severity === 'warn') warn++;
        else if (m.severity === 'fail') fail++;
        else if (m.severity === 'info') info++;
        else error++;
      }
    }
    return { pass, warn, fail, info, error };
  }, [agents]);

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    for (const a of agents) {
      for (const m of a.modules) {
        if (findingKey(a.id, m.nodeId) === selectedKey) return { agent: a, module: m };
      }
    }
    return null;
  }, [selectedKey, agents]);

  // Lazy detail: the tree bootstraps from compliance_scores; the full audit blob
  // (real per-check breakdowns) loads per agent only when its detail is viewed.
  const detailLoading = useRef<Set<string>>(new Set());
  const loadAgentDetail = useCallback(async (agentId: string) => {
    const agent = agents.find(a => a.id === agentId);
    if (!agent || !agent.executionId || agent.detailLoaded || detailLoading.current.has(agentId)) return;
    detailLoading.current.add(agentId);
    try {
      const detail = await api.getAuditResult(agent.executionId);
      const audit = detail?.data || detail;
      const modules = parseAuditModules(audit).map((m): ModuleNode => ({
        ...m,
        severity: normalizeStatus(m.status),
        checks: parseModuleChecks(m.details),
      }));
      modules.sort((x, y) =>
        SEVERITY_RANK[x.severity] - SEVERITY_RANK[y.severity] || x.name.localeCompare(y.name)
      );
      setAgents(prev => prev.map(a => a.id === agentId
        ? {
            ...a,
            modules: modules.length > 0 ? modules : a.modules,
            lastRun: audit?.completed_at || audit?.started_at || a.lastRun,
            detailLoaded: true,
          }
        : a));
    } catch {
      // Best-effort enrichment — the score-derived modules stay usable
    } finally {
      detailLoading.current.delete(agentId);
    }
  }, [agents]);

  // Selecting any module (click, jump chip, Fix Next, initial auto-select) upgrades
  // that agent's detail; the detailLoaded guard makes re-runs no-ops.
  useEffect(() => {
    if (selected && !selected.agent.detailLoaded) loadAgentDetail(selected.agent.id);
  }, [selected, loadAgentDetail]);

  const jumpTo = useCallback((key: string, agentId: string) => {
    setExpandedAgents(prev => new Set(prev).add(agentId));
    setExpandedModules(prev => new Set(prev).add(key));
    setSelectedKey(key);
    // Allow the expand to render before scrolling
    setTimeout(() => rowRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  }, []);

  const jumpToAgent = useCallback((agentId: string) => {
    setExpandedAgents(prev => new Set(prev).add(agentId));
    setTimeout(() => rowRefs.current[`agent::${agentId}`]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  }, []);

  const fixNext = useCallback(() => {
    const next = actionable.find(f => !resolved.has(f.key));
    if (!next) {
      showToast('All findings addressed', 'success');
      return;
    }
    jumpTo(next.key, next.agent.id);
  }, [actionable, resolved, jumpTo, showToast]);

  const markResolved = useCallback((key: string) => {
    setResolved(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    const remaining = actionable.filter(f => f.key !== key && !resolved.has(f.key));
    if (remaining.length > 0) {
      jumpTo(remaining[0].key, remaining[0].agent.id);
    }
  }, [actionable, resolved, jumpTo]);

  const rerunOnAgent = useCallback(async (agent: AgentNode) => {
    try {
      await api.synchronizeAgent(agent.id);
      showToast(`Audit queued on ${agent.hostname}`, 'success');
    } catch (err: any) {
      showToast(err?.message || 'Failed to queue audit', 'error');
    }
  }, [showToast]);

  const runFleetAudit = useCallback(async () => {
    const online = agents.filter(a => a.online);
    if (online.length === 0) {
      showToast('No online agents to audit', 'error');
      return;
    }
    // Per-agent (not synchronize-fleet: that endpoint is tenant-wide, this page is one client).
    // allSettled-style so partial failure reports what actually happened on customer machines.
    const results = await mapPool(online, 5, a =>
      api.synchronizeAgent(a.id).then(() => true).catch(() => false)
    );
    const ok = results.filter(Boolean).length;
    if (ok === online.length) {
      showToast(`Audit queued on ${ok} online agent${ok === 1 ? '' : 's'}`, 'success');
    } else if (ok > 0) {
      showToast(`Audit queued on ${ok} of ${online.length} agents — ${online.length - ok} failed`, 'error');
    } else {
      showToast('Fleet audit failed — no agents queued', 'error');
    }
    if (ok > 0) {
      // Cooldown: turbo mode fires on next heartbeat, so immediate re-clicks only confuse
      setFleetQueued(true);
      fleetTimer.current = setTimeout(() => setFleetQueued(false), 60_000);
    }
  }, [agents, showToast]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-anchor-blue"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="bg-anchor-card border border-red-400/20 rounded-xl p-8 text-center">
        <p className="text-red-400 text-sm">{loadError}</p>
      </div>
    );
  }

  const clientName = clientInfo?.clientName || 'Client';
  const clientScore = clientInfo?.avgScore || 0;
  const { grade, color } = scoreToGrade(clientScore);
  const onlineCount = agents.filter(a => a.online).length;

  return (
    <div className="space-y-4 pb-16">
      {/* Header — hidden in embedded mode (PostureTriagePage's client switcher replaces it) */}
      {!embedded && (
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/dashboard')} className="text-white/40 hover:text-white transition-colors" aria-label="Back to dashboard">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        {clientScore > 0 && <PixelGradeBadge score={clientScore} size={44} />}
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-medium text-white">{clientName}</h2>
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/[0.06] text-white/40 border border-white/[0.06]">Client</span>
          </div>
          <p className="text-sm text-white/40">
            {agents.length} agent{agents.length === 1 ? '' : 's'} · {onlineCount} online
            {clientScore > 0 && <span> · <span style={{ color }}>{grade}</span> · {clientScore}%</span>}
          </p>
        </div>
        <button
          onClick={runFleetAudit}
          disabled={fleetQueued}
          className="ml-auto px-4 py-2 rounded-lg bg-anchor-blue/20 text-anchor-blue text-sm font-medium hover:bg-anchor-blue/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {fleetQueued ? '✓ Fleet audit queued' : '↻ Run fleet audit'}
        </button>
      </div>
      )}

      {/* Needs-attention triage banner */}
      {(actionable.length > 0 || loadFailures.length > 0) && (
        <div className="flex items-center gap-3 bg-anchor-card border border-yellow-400/20 rounded-xl px-4 py-2.5 flex-wrap">
          <span className="text-sm text-yellow-400 font-medium whitespace-nowrap">
            ⚠ Needs attention: {rollup.fail} failed · {rollup.warn} warning{rollup.warn === 1 ? '' : 's'}{loadFailures.length > 0 ? ` · ${loadFailures.length} unavailable` : ''}
          </span>
          <div className="flex gap-1.5 flex-wrap flex-1">
            {actionable.slice(0, 6).map(f => {
              const st = severityStyles[f.module.severity];
              const isResolved = resolved.has(f.key);
              return (
                <button
                  key={f.key}
                  onClick={() => jumpTo(f.key, f.agent.id)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    isResolved
                      ? 'bg-green-400/10 text-green-400/60 border-green-400/10 line-through'
                      : `${st.bg} ${st.text} border-white/[0.06] hover:border-white/[0.2]`
                  }`}
                >
                  {f.module.name} <span className="opacity-60">@{f.agent.hostname}</span>
                </button>
              );
            })}
            {loadFailures.slice(0, 4).map(a => (
              <button
                key={`lf-${a.id}`}
                onClick={() => jumpToAgent(a.id)}
                className="text-[11px] px-2.5 py-1 rounded-full border bg-blue-400/10 text-blue-400 border-white/[0.06] hover:border-white/[0.2] transition-colors"
              >
                No audit data <span className="opacity-60">@{a.hostname}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-anchor-blue font-medium">{resolvedCount} of {actionable.length} resolved</span>
            <div className="w-24 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
              <div className="h-full bg-anchor-blue rounded-full transition-all" style={{ width: `${actionable.length ? (resolvedCount / actionable.length) * 100 : 0}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Tree + detail panel */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-start">
        {/* Treeview */}
        <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
          <div className="grid grid-cols-[1fr_110px_90px] px-4 py-2 border-b border-white/[0.06] text-[10px] font-medium text-white/30 uppercase tracking-wider">
            <span>Agent / Module / Check</span>
            <span>Result</span>
            <span className="text-right">Last run</span>
          </div>

          {agents.length === 0 && (
            <div className="p-8 text-center text-white/30 text-sm">No agents enrolled for this client yet.</div>
          )}

          {agents.map(agent => {
            const agentExpanded = expandedAgents.has(agent.id);
            const g = agent.score > 0 ? scoreToGrade(agent.score) : null;
            const nonPassing = agent.modules.filter(m => m.severity !== 'pass');
            const passing = agent.modules.filter(m => m.severity === 'pass');
            const counts = {
              fail: agent.modules.filter(m => m.severity === 'fail').length,
              warn: agent.modules.filter(m => m.severity === 'warn').length,
              pass: passing.length,
            };
            return (
              <div key={agent.id} ref={el => { rowRefs.current[`agent::${agent.id}`] = el; }} className={agent.online ? '' : 'opacity-50'}>
                {/* Agent row */}
                <button
                  onClick={() => setExpandedAgents(prev => {
                    const next = new Set(prev);
                    if (next.has(agent.id)) { next.delete(agent.id); } else { next.add(agent.id); loadAgentDetail(agent.id); }
                    return next;
                  })}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 bg-white/[0.03] border-b border-white/[0.04] hover:bg-white/[0.05] transition-colors text-left"
                >
                  <span className="text-white/25 text-[10px] w-3">{agentExpanded ? '▼' : '▶'}</span>
                  <span className={`w-2 h-2 rounded-full ${agent.online ? 'bg-green-400' : 'bg-white/20'}`} />
                  <span className="text-sm font-semibold text-white tracking-wide">{agent.hostname}</span>
                  {agent.agentVersion && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/40">agent {agent.agentVersion}</span>
                  )}
                  {g && (
                    <span className="text-[11px] font-bold px-1.5 py-0.5 rounded" style={{ color: g.color, backgroundColor: `${g.color}1a` }}>{g.grade}</span>
                  )}
                  {!agent.online && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/40 uppercase">stale · offline {daysAgo(agent.lastSeenAt)}</span>
                  )}
                  <span className="ml-auto flex items-center gap-2 text-[11px]">
                    {counts.fail > 0 && <span className="text-red-400">✗ {counts.fail}</span>}
                    {counts.warn > 0 && <span className="text-yellow-400">! {counts.warn}</span>}
                    <span className="text-green-400">✓ {counts.pass}</span>
                    <span className="text-white/25">seen {daysAgo(agent.lastSeenAt)}</span>
                  </span>
                </button>

                {/* Module rows */}
                {agentExpanded && (
                  <div>
                    {agent.auditError && (
                      <div className="px-9 py-2 text-xs text-red-400/70 border-b border-white/[0.04]">{agent.auditError}</div>
                    )}
                    {agent.modules.length === 0 && !agent.auditError && (
                      <div className="px-9 py-2 text-xs text-white/25 border-b border-white/[0.04]">No audit results yet.</div>
                    )}
                    {nonPassing.map(mod => {
                      const key = findingKey(agent.id, mod.nodeId);
                      const modExpanded = expandedModules.has(key);
                      const st = severityStyles[mod.severity];
                      const isSelected = selectedKey === key;
                      const isResolved = resolved.has(key);
                      // The fact (name) reads better as a row summary than the
                      // requirement clause that now lives in description
                      const detailSummary = mod.errorMessage
                        || mod.checks.find(c => c.status === 'fail')?.name
                        || mod.checks.find(c => c.status === 'warn')?.name
                        || '';
                      return (
                        <div key={key} ref={el => { rowRefs.current[key] = el; }}>
                          <div
                            className={`grid grid-cols-[1fr_110px_90px] items-center px-4 py-2 border-b border-white/[0.04] cursor-pointer transition-colors ${
                              isSelected ? 'bg-anchor-blue/10 border-l-2 border-l-anchor-blue' : 'hover:bg-white/[0.02] border-l-2 border-l-transparent'
                            }`}
                            onClick={() => { setSelectedKey(key); setExpandedModules(prev => { const n = new Set(prev); if (n.has(key)) { n.delete(key); } else { n.add(key); } return n; }); }}
                          >
                            <span className="flex items-center gap-2 pl-5">
                              <span className="text-white/25 text-[10px] w-3">{mod.checks.length > 0 ? (modExpanded ? '▼' : '▶') : ''}</span>
                              <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                              <span className="text-sm text-white/85">{mod.name}</span>
                              {detailSummary && <span className="text-[11px] text-white/30 truncate">{detailSummary}</span>}
                              {isResolved && <span className="text-[10px] text-green-400">✓ resolved</span>}
                            </span>
                            <span className={`text-xs font-medium ${st.text}`}>{st.label}</span>
                            <span className="text-[11px] text-white/30 text-right">{daysAgo(agent.lastRun)}</span>
                          </div>
                          {modExpanded && mod.checks.map((check, ci) => (
                            <div key={ci} className="grid grid-cols-[1fr_200px] items-center pl-14 pr-4 py-1.5 border-b border-white/[0.03] bg-white/[0.01]">
                              <span className="flex items-center gap-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${checkDot[check.status]}`} />
                                <span className="text-xs text-white/60">{check.name}</span>
                              </span>
                              <span className="text-[11px] text-white/35 truncate">{check.value || check.status}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {/* Passing modules collapsed into one toggle row */}
                    {passing.length > 0 && (
                      <button
                        onClick={() => setShowPassing(prev => { const n = new Set(prev); if (n.has(agent.id)) { n.delete(agent.id); } else { n.add(agent.id); } return n; })}
                        className="w-full text-left px-9 py-2 text-xs text-white/30 hover:text-white/50 border-b border-white/[0.04] transition-colors"
                      >
                        {showPassing.has(agent.id) ? '▼' : '▶'} {passing.length} passing module{passing.length === 1 ? '' : 's'}
                      </button>
                    )}
                    {showPassing.has(agent.id) && passing.map(mod => {
                      const key = findingKey(agent.id, mod.nodeId);
                      const isSelected = selectedKey === key;
                      return (
                        <div
                          key={key}
                          onClick={() => setSelectedKey(key)}
                          className={`grid grid-cols-[1fr_110px_90px] items-center px-4 py-1.5 border-b border-white/[0.03] cursor-pointer transition-colors ${
                            isSelected ? 'bg-anchor-blue/10' : 'hover:bg-white/[0.02]'
                          }`}
                        >
                          <span className="flex items-center gap-2 pl-8">
                            <span className="w-2 h-2 rounded-full bg-green-400" />
                            <span className="text-xs text-white/55">{mod.name}</span>
                          </span>
                          <span className="text-[11px] font-medium text-green-400">PASS</span>
                          <span className="text-[11px] text-white/25 text-right">{daysAgo(agent.lastRun)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-5 xl:sticky xl:top-20">
          {!selected ? (
            <p className="text-white/30 text-sm text-center py-10">Select a module in the tree to see details.</p>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2.5 h-2.5 rounded-full ${severityStyles[selected.module.severity].dot}`} />
                  <h3 className="text-lg font-medium text-white">{selected.module.name}</h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${severityStyles[selected.module.severity].bg} ${severityStyles[selected.module.severity].text}`}>
                    {severityStyles[selected.module.severity].label}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/50">🖥 {selected.agent.hostname}</span>
                  <span className="text-[11px] text-white/30">last run {daysAgo(selected.agent.lastRun)} · {selected.module.checks.length} checks</span>
                </div>
              </div>

              {selected.module.errorMessage && (
                <p className="text-xs text-red-400/80">{selected.module.errorMessage}</p>
              )}

              {selected.module.checks.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-medium text-white/30 uppercase tracking-wider mb-2">Checks ({selected.module.checks.length})</h4>
                  <div className="space-y-2">
                    {selected.module.checks.map((check, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 ${checkDot[check.status]}`} />
                        <div className="min-w-0">
                          <div className="text-xs text-white/75">{check.name}</div>
                          {check.value && <div className="text-[11px] text-white/45 break-words">{check.value}</div>}
                          {check.description && <div className="text-[10px] text-white/25 italic break-words">{check.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selected.module.severity !== 'pass' && (
                <div className={`rounded-lg border p-3 ${selected.module.severity === 'fail' ? 'border-red-400/20 bg-red-400/[0.04]' : 'border-yellow-400/20 bg-yellow-400/[0.04]'}`}>
                  <h4 className="text-[10px] font-medium text-white/40 uppercase tracking-wider mb-1.5">
                    🔧 Remediation{selected.module.severity === 'fail' ? ' — high priority' : ''}
                  </h4>
                  <p className="text-xs text-white/60 leading-relaxed">{remediationFor(selected.module.name)}</p>
                </div>
              )}

              <div className="space-y-2 pt-1">
                {selected.module.severity !== 'pass' && selected.module.severity !== 'info' && (
                  <button
                    onClick={() => markResolved(findingKey(selected.agent.id, selected.module.nodeId))}
                    disabled={resolved.has(findingKey(selected.agent.id, selected.module.nodeId))}
                    className="w-full py-2 rounded-lg bg-anchor-blue/20 text-anchor-blue text-sm font-medium hover:bg-anchor-blue/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {resolved.has(findingKey(selected.agent.id, selected.module.nodeId)) ? '✓ Marked resolved' : 'Mark resolved'}
                  </button>
                )}
                <button
                  onClick={() => rerunOnAgent(selected.agent)}
                  disabled={!selected.agent.online}
                  className="w-full py-2 rounded-lg bg-white/[0.04] text-white/60 text-sm hover:bg-white/[0.08] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ↻ Re-run on {selected.agent.hostname}{!selected.agent.online ? ' (offline)' : ''}
                </button>
                <button
                  onClick={() => navigate(`/agents/${selected.agent.id}`)}
                  className="w-full py-2 rounded-lg bg-white/[0.04] text-white/60 text-sm hover:bg-white/[0.08] transition-colors"
                >
                  Open agent page →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer rollup bar */}
      <div className="fixed bottom-0 left-64 right-0 z-10 bg-anchor-navy/85 backdrop-blur-sm border-t border-white/[0.06] px-8 py-2.5 flex items-center gap-4">
        <span className="text-sm text-white font-medium">{agents.length} agent{agents.length === 1 ? '' : 's'}</span>
        <span className="text-xs text-green-400">✓ {rollup.pass} passed</span>
        {rollup.warn > 0 && <span className="text-xs text-yellow-400">! {rollup.warn} warning{rollup.warn === 1 ? '' : 's'}</span>}
        {rollup.fail > 0 && <span className="text-xs text-red-400">✗ {rollup.fail} failed</span>}
        {rollup.info > 0 && <span className="text-xs text-sky-300">ℹ {rollup.info} info</span>}
        {rollup.error > 0 && <span className="text-xs text-blue-400">⚠ {rollup.error} error{rollup.error === 1 ? '' : 's'}</span>}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={runFleetAudit}
            disabled={fleetQueued}
            className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/60 text-xs hover:bg-white/[0.08] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {fleetQueued ? '✓ Queued' : '↻ Run fleet audit'}
          </button>
          {actionable.length > 0 && resolvedCount < actionable.length && (
            <button
              onClick={fixNext}
              className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
            >
              ⚡ Fix Next Issue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetailPage;
